// DEPENDENCIES ---------------------------------------------------------------- //

import { Moves } from '@gql/base-types';

// TYPES ---------------------------------------------------------------- //

interface ConfirmationFlowObject {
  pickup?: unknown[];
  delivery?: unknown[];
}

// UTILITY ---------------------------------------------------------------- //

/**
 * Checks if a confirmation_flow has a pickup property
 */
export const hasPickupFlow = (confirmationFlow: unknown): boolean => {
  if (!confirmationFlow) return false;

  let flow = confirmationFlow;

  // Handle string JSON (in case GraphQL returns it as a string)
  if (typeof flow === 'string') {
    try {
      flow = JSON.parse(flow);
    } catch (e) {
      return false;
    }
  }

  // Check if it's an object with pickup property
  if (flow && typeof flow === 'object' && !Array.isArray(flow)) {
    const flowObj = flow as ConfirmationFlowObject;
    return flowObj.pickup !== undefined && flowObj.pickup !== null;
  }

  // Legacy array format means both pickup and delivery use the same flow
  if (Array.isArray(flow) && flow.length > 0) {
    return true;
  }

  return false;
};

/**
 * Checks if a confirmation_flow has a delivery property
 */
export const hasDeliveryFlow = (confirmationFlow: unknown): boolean => {
  if (!confirmationFlow) return false;

  let flow = confirmationFlow;

  // Handle string JSON (in case GraphQL returns it as a string)
  if (typeof flow === 'string') {
    try {
      flow = JSON.parse(flow);
    } catch (e) {
      return false;
    }
  }

  // Check if it's an object with delivery property
  if (flow && typeof flow === 'object' && !Array.isArray(flow)) {
    const flowObj = flow as ConfirmationFlowObject;
    return flowObj.delivery !== undefined && flowObj.delivery !== null;
  }

  // Legacy array format means both pickup and delivery use the same flow
  if (Array.isArray(flow) && flow.length > 0) {
    return true;
  }

  return false;
};

/**
 * Gets the confirmationset from the move, prioritizing workflowset over confirmationrequests
 */
const getConfirmationset = (move: Moves | null | undefined): { confirmation_flow?: unknown } | null => {
  if (!move) return null;

  // First, try to get confirmationset from workflowset (available even before requests are created)
  const confirmationsetFromWorkflowset = move.workflowset?.confirmationset;
  if (confirmationsetFromWorkflowset) {
    return confirmationsetFromWorkflowset;
  }

  // Fallback: get confirmationset from confirmationrequests (for backwards compatibility)
  const scenarioToMove = move.scenariotomoves?.[0];
  if (!scenarioToMove?.scenario) return null;

  const scenario = scenarioToMove.scenario;
  const confirmationRequests = scenario.confirmationrequests || [];
  const confirmationsetFromRequests = confirmationRequests[0]?.confirmationset;

  return confirmationsetFromRequests || null;
};

/**
 * Checks if pickup requires confirmation based on confirmation_flow
 */
export const requiresPickupConfirmation = (move: Moves | null | undefined): boolean => {
  if (!move) return false;

  const confirmationset = getConfirmationset(move);
  if (!confirmationset?.confirmation_flow) return false;

  return hasPickupFlow(confirmationset.confirmation_flow);
};

/**
 * Checks if delivery requires confirmation based on confirmation_flow
 */
export const requiresDeliveryConfirmation = (move: Moves | null | undefined): boolean => {
  if (!move) return false;

  const confirmationset = getConfirmationset(move);
  if (!confirmationset?.confirmation_flow) return false;

  return hasDeliveryFlow(confirmationset.confirmation_flow);
};

/**
 * Checks if either pickup or delivery requires confirmation
 */
export const requiresAnyConfirmation = (move: Moves | null | undefined): boolean => {
  return requiresPickupConfirmation(move) || requiresDeliveryConfirmation(move);
};
